import { type FeedbackSendingMethod, updateFeedbackRequest } from '@aikoa/common'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUserId } from '~/components/user/SessionProvider'
import { supabase } from '~/lib/supabase'
import { trpc } from '~/lib/trpc'
import { getFeedback, getLatestRespondedFeedback } from './readFeedback'

export const useSubmitFeedback = () => {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation(
    trpc.feedback.submitFeedbackAnswers.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: [userId, 'feedbackItems'],
        })
        void queryClient.invalidateQueries({ queryKey: ['feedbackSurveyData'] })
      },
    })
  )
}

export const useSaveFeedbackProgress = () => {
  return useMutation(trpc.feedback.saveFeedbackProgress.mutationOptions())
}

export const useSetFeedbackInactive = () => {
  return useMutation(trpc.feedback.setFeedbackInactive.mutationOptions())
}

export const useUndoFeedbackInactive = () => {
  return useMutation(trpc.feedback.undoFeedbackInactive.mutationOptions())
}

export const useAdoptResponder = () => {
  return useMutation(trpc.feedback.adoptResponder.mutationOptions())
}

export function useLatestRespondedFeedback() {
  const userId = useUserId()

  return useQuery({
    queryKey: [userId, 'latestRespondedFeedback'],
    queryFn: () => (userId ? getLatestRespondedFeedback(userId) : null),
    enabled: !!userId,
  })
}

export const useSubmitBuyerSearchUpdate = () => {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation(
    trpc.feedback.submitBuyerSearchUpdate.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['interests', userId] })
        void queryClient.invalidateQueries({ queryKey: [userId, 'latestRespondedFeedback'] })
      },
    })
  )
}

export const useSubmitRealtorLinkFeedback = () => {
  return useMutation(trpc.feedback.submitRealtorLinkFeedback.mutationOptions())
}

export const useFeedbackItems = ({
  contactIds,
  answeredOnly,
}: {
  contactIds?: string | string[]
  answeredOnly?: boolean
} = {}) => {
  const userId = useUserId()

  return useQuery({
    queryKey: [
      userId,
      'feedbackItems',
      contactIds ?? 'all contacts',
      answeredOnly ? 'answered' : 'all',
    ].filter(Boolean),
    queryFn: () =>
      userId
        ? getFeedback({
            userId,
            contactIds,
            answeredOnly,
          })
        : [],
    enabled: !!userId,
  })
}

type UpdateFeedbackRequestArgs = {
  feedbackId: string
  sendAt?: Date | null
  sendingMethod?: FeedbackSendingMethod
}

export const useUpdateFeedbackRequest = () => {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: async ({ feedbackId, sendAt, sendingMethod }: UpdateFeedbackRequestArgs) => {
      await updateFeedbackRequest({ feedbackId, sendAt, sendingMethod }, supabase)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['feedback', variables.feedbackId],
      })

      if (userId) {
        void queryClient.invalidateQueries({ queryKey: [userId, 'feedbackItems'] })
      }
    },
  })
}
